import { makeAutoObservable, runInAction } from "mobx";
import { v4 as uuidv4 } from "uuid";
import { BackendError } from "../../../config/axios";
import { LABS } from "../../../constants/AppsConstants";
import { IRootStore } from "../../../mobx/root-store";
import { PlaygroundResult } from "../../../models/playground";
import {
	TgVideoEditingConfigType,
	VIDEO_EDITING_ASYNC_PROPS,
	VideoEditingConfigType,
	VideoEditingResponse,
	VideoEditingTools,
	VideoGenerationResponse,
	defaultTgVideoEditingConfig,
	defaultVideoEditingConfig,
} from "../../../models/video-editing";
import QueryService from "../../../utils/QueryService";
import useImageUtils from "../../../utils/useImageUtils";
import { VideoMetadata, VideoValidationErrors } from "../../Labs/VideoEditing/hooks/useVideoEditingConfig";
import useVideoGenerationError, {
	VideoConstraints,
} from "../../Playground/VideoGeneration/Error/useVideoGenerationError";

export interface IVideoEditingStore {
	config: VideoEditingConfigType;
	tgConfig: TgVideoEditingConfigType;
	errors: {
		editVideo?: BackendError;
		upload?: BackendError | VideoValidationErrors;
		increase_resolution?: VideoValidationErrors;
		remove_background?: VideoValidationErrors;
		solid_background?: VideoValidationErrors;
		erase?: VideoValidationErrors;
	};
	loadingGenerate: boolean;
	loadingEdit: boolean;
	lastGeneratedResultId: string | null;

	handleConfigChange: <K extends keyof VideoEditingConfigType>(key: K, value: VideoEditingConfigType[K]) => void;
	handleTgConfigChange: <K extends keyof TgVideoEditingConfigType>(
		key: K,
		value: TgVideoEditingConfigType[K],
	) => void;
	generateTgVideo: (config: TgVideoEditingConfigType) => Promise<VideoGenerationResponse>;
	editVideo: (config?: VideoEditingConfigType) => Promise<VideoEditingResponse>;
	validateAllTools: (
		videoUrl: string,
		metadata: VideoMetadata,
		validateVideoConstraints: (metadata: VideoMetadata, constraints: VideoConstraints) => VideoValidationErrors,
	) => Promise<void>;

	clearErrors: () => void;
	abortVideoEdit: () => void;
}

export class VideoEditingStore implements IVideoEditingStore {
	private rootStore: IRootStore;
	private queryService: QueryService = new QueryService("/video/edit");
	private imageUtils = useImageUtils();
	private videoGenerationError = useVideoGenerationError();
	private abortController: AbortController | null = null;

	config: VideoEditingConfigType = defaultVideoEditingConfig;
	tgConfig: TgVideoEditingConfigType = defaultTgVideoEditingConfig;

	loadingGenerate: boolean = false;
	loadingEdit: boolean = false;
	lastGeneratedResultId: string | null = null;
	videoMetadata: VideoMetadata | null = null;

	errors: {
		editVideo?: BackendError;
		upload?: BackendError | VideoValidationErrors;
		increase_resolution?: VideoValidationErrors;
		remove_background?: VideoValidationErrors;
		solid_background?: VideoValidationErrors;
		erase?: VideoValidationErrors;
	} = {};

	constructor(rootStore: IRootStore) {
		makeAutoObservable(this);
		this.rootStore = rootStore;
	}

	handleConfigChange = <K extends keyof VideoEditingConfigType>(key: K, value: VideoEditingConfigType[K]) => {
		runInAction(() => {
			this.config = { ...this.config, [key]: value };
			if (key === "video" && !value) {
				this.videoMetadata = null;
				this._clearToolErrors();
			}
		});
	};

	handleTgConfigChange = <K extends keyof TgVideoEditingConfigType>(key: K, value: TgVideoEditingConfigType[K]) => {
		runInAction(() => {
			this.tgConfig = { ...this.tgConfig, [key]: value };
		});
	};

	generateTgVideo = async (config: TgVideoEditingConfigType): Promise<VideoGenerationResponse> => {
		try {
			const { playgroundStore } = this.rootStore;
			this.loadingGenerate = true;
			const response: VideoGenerationResponse = await this.queryService.post(`/`, config);
			runInAction(() => {
				this.loadingGenerate = false;
				playgroundStore.playgroundResults = [...playgroundStore.playgroundResults];
			});

			return response;
		} catch (err: any) {
			this.loadingGenerate = false;
			return Promise.reject(err);
		}
	};

	editVideo = async (config?: VideoEditingConfigType): Promise<VideoEditingResponse> => {
		const { playgroundStore, authStore, anonymousUsageStore } = this.rootStore;
		const _config: VideoEditingConfigType = { ...(config || this.config) };

		if (!_config.tool) {
			throw new Error("Tool is required");
		}

		const anonymousFeature = `labs_video_${_config.tool}`;

		// Check anonymous usage limit before starting
		if (!authStore.isLoggedIn) {
			const remaining = anonymousUsageStore.getRemaining(anonymousFeature);
			if (remaining <= 0) {
				anonymousUsageStore.setLimitReachedFeature(anonymousFeature);
				return Promise.reject(new Error("Usage limit reached"));
			}
		}

		this._initializeEditVideo(["editVideo"]);
		this.loadingEdit = true;

		const resultIndex = this._showLoadingSkeleton(_config);

		try {
			const statusResult: { status_url: string } = await this.queryService.post(`/`, {
				..._config,
				tool_config: {
					[_config.tool]: _config.tool_config?.[_config.tool],
				},
			});

			const result: { video_url: string } = await this.imageUtils.pullUntilAvailableV2("", {
				statusUrl: statusResult.status_url,
				isV2: true,
				...VIDEO_EDITING_ASYNC_PROPS[_config.tool],
				abortController: this.abortController,
			});

			playgroundStore.playgroundResults[resultIndex].video = {
				src: result.video_url,
				thumbnail: "",
				loading: false,
				asyncProps: VIDEO_EDITING_ASYNC_PROPS[_config.tool],
			};
			this.loadingEdit = false;

			// Consume an anonymous token when the video is actually delivered
			if (!authStore.isLoggedIn) {
				anonymousUsageStore.consumeToken(anonymousFeature);
			}

			return result;
		} catch (err: any) {
			this.loadingEdit = false;

			// Handle anonymous usage limit error
			const isAnonymousUsageLimit =
				err?.response?.status === 429 && err?.response?.data?.code === "ANONYMOUS_USAGE_LIMIT_REACHED";
			if (isAnonymousUsageLimit) {
				const feature = err?.response?.data?.feature || anonymousFeature;
				this._removeResultAtIndex(resultIndex);
				this.rootStore.anonymousUsageStore.setLimitReachedFeature(feature);
				return Promise.reject(err);
			}

			this._handleError(err, "editVideo", "Video editing failed");
			this._removeResultAtIndex(resultIndex);
			return Promise.reject(err);
		}
	};

	clearErrors = () => {
		this.errors = {};
		this.rootStore.videoGenerationStore.errors = {};
	};

	validateAllTools = async (
		_videoUrl: string,
		metadata: VideoMetadata,
		validateVideoConstraints: (metadata: VideoMetadata, constraints: VideoConstraints) => VideoValidationErrors,
	) => {
		runInAction(() => {
			this.videoMetadata = metadata;
			this._clearToolErrors();
		});

		const tools: VideoEditingTools[] = ["erase", "remove_background", "solid_background", "increase_resolution"];

		const validationResults = tools.map((tool) => {
			const constraints = this.videoGenerationError.getVideoConstraints(tool);
			const errors = validateVideoConstraints(metadata, constraints);
			return {
				tool,
				errors: errors.duration || errors.fps ? errors : undefined,
			};
		});

		runInAction(() => {
			validationResults.forEach(({ tool, errors }) => {
				if (errors) {
					const toolKey = tool as "erase" | "remove_background" | "solid_background" | "increase_resolution";
					this.errors[toolKey] = errors;
				}
			});
		});
	};

	private _clearToolErrors = () => {
		this.errors.increase_resolution = undefined;
		this.errors.remove_background = undefined;
		this.errors.solid_background = undefined;
		this.errors.erase = undefined;
	};

	abortVideoEdit = () => {
		const { playgroundStore } = this.rootStore;

		if (this.abortController) {
			this.abortController.abort();
		}

		this.loadingEdit = false;
		playgroundStore.removeNotLoadingImages();
		const lastResult = this.lastGeneratedResultId
			? playgroundStore.playgroundResults.find((r) => r.id === this.lastGeneratedResultId)
			: null;

		if (lastResult) {
			playgroundStore.selectedResult = lastResult;
		}
	};

	private _showLoadingSkeleton(config: VideoEditingConfigType): number {
		const { playgroundStore } = this.rootStore;
		const resultSkeleton: PlaygroundResult = {
			id: uuidv4(),
			config: { ...config } as VideoEditingConfigType,
			type: LABS.VIDEO_EDITING,
			images: [],
			video: {
				src: "",
				thumbnail: "",
				loading: true,
				asyncProps: VIDEO_EDITING_ASYNC_PROPS[config.tool as VideoEditingTools],
			},
		};
		playgroundStore.playgroundResults = [...playgroundStore.playgroundResults, resultSkeleton];

		const savedIndex = playgroundStore.playgroundResults.length - 1;
		return savedIndex;
	}

	private _removeResultAtIndex(index: number): void {
		this.rootStore.playgroundStore.playgroundResults.splice(index, 1);
	}

	private _initializeEditVideo(errorKeys: Array<keyof typeof this.errors>): void {
		this.abortController = new AbortController();
		this.errors = errorKeys.reduce((acc, key) => ({ ...acc, [key]: undefined }), { ...this.errors });
	}

	private _handleError(
		err: any,
		errorKey: "editVideo" | "upload",
		defaultMessage: string,
		onAbort?: () => void,
	): void {
		if (this.abortController?.signal?.aborted) {
			onAbort?.();
			return;
		}
		this._setError(errorKey, err, defaultMessage);
	}

	private _setError(key: "editVideo" | "upload", err: any, defaultMessage: string): void {
		this.errors[key] = {
			message: err.response?.data?.message || err.message || defaultMessage,
			status: err.response?.status || 500,
		};
	}
}
